import { useState, useEffect } from "react"
import { WizardStep } from "../../ui/wizard"
import { NeutralButton } from "../../common/Buttons"
import { createListCollection, Input } from "@chakra-ui/react"
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "../../ui/select"
import { Radio, RadioGroup } from "../../ui/radio"
import { colors } from "../../../utilities/colors"
import AuditUnitDetails from "./AuditUnitDetails"
import auditConditionEnum from "../../../constants/auditConditionEnum"
import auditLocationEnum from "../../../constants/auditLocationEnum"
import {
  conditionColors,
  canRefloor,
  isMaxRefloored,
  getDefaultLocation,
  isOnTheLot,
  isLocationSelectable
} from "../../../utilities/auditUtilities"

export default function AuditUnitCondition({ auditUnit, auditData, setAuditData, nextStep }) {
  const [formValues, setFormValues] = useState({
    condition: [auditData.condition],
    can_refloor: auditData.can_refloor,
    current_location: getDefaultLocation(auditData),
    location: auditData.location ?? '',
  })

  useEffect(() => {
    setFormValues({
      condition: [1,2,3,4].includes(Number(auditData.condition)) ? [auditData.condition] : [],
      can_refloor: auditData.can_refloor,
      current_location: getDefaultLocation(auditData),
      location: auditData.location ?? '',
    })
  }, [auditData])

  const selectOptions = createListCollection({
    items: Object.keys(auditConditionEnum).map((condition) =>
      ({ label: auditConditionEnum[condition], value: condition })
    )
  })

  // Frontline Ready units are always On the Lot, so the location prompt is skipped for them
  const isLocationPrompted = formValues.condition[0] && formValues.condition[0] !== '1'
  const selectedLocation = isLocationPrompted ? formValues.current_location : '1'
  const isAtAuction = String(selectedLocation) === '3'

  const locationOptions = Object.entries(auditLocationEnum).filter(([location]) =>
    isLocationSelectable(location, formValues.condition[0])
  )

  const handleConditionChange = (condition) => {
    setFormValues({
      ...formValues,
      condition: condition,
      can_refloor: canRefloor(condition[0], auditUnit),
      current_location: isLocationSelectable(formValues.current_location, condition[0])
        ? formValues.current_location
        : '2',
    })
  }

  const handleNextStep = () => {
    setAuditData({
      ...auditData,
      condition: formValues.condition[0],
      can_refloor: formValues.can_refloor,
      current_location: selectedLocation,
      location: isOnTheLot(selectedLocation) ? '' : formValues.location,
    })
    nextStep()
  }

  return (
    <WizardStep>
      <div className="flex flex-col items-center w-full h-full">
        <div className="flex flex-col items-center justify-between w-full md:w-4/5 h-full pt-6 px-2">
          <AuditUnitDetails auditUnit={auditUnit} />

          <div className="flex flex-col items-center w-full px-2">
            <div className='flex flex-col items-center w-full m-2'>
              <h1 className="text-2xl font-bold">Unit Condition</h1>
              <SelectRoot
                collection={selectOptions}
                size="sm"
                positioning={{ sameWidth: true, placement: "bottom" }}
                value={formValues.condition}
                onValueChange={(e) => handleConditionChange(e.value)}
              >
                <SelectTrigger
                  borderWidth={2}
                  borderColor={colors.grey}
                  _focus={{ borderColor: colors.grey300 }}
                  rounded="md"
                  p={2}
                >
                  <SelectValueText placeholder="Select Condition">
                    {(items) => items.map((item) => (
                      <span key={item.value} className={conditionColors(item.value)}>
                        {item.label}
                      </span>
                    ))}
                  </SelectValueText>
                </SelectTrigger>
                <SelectContent portalled={false} width="full">
                  {selectOptions.items.map((option) => (
                    <SelectItem key={option.value} item={option} py={3}>
                      <span className={conditionColors(option.value)}>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            {isLocationPrompted &&
              <div className='flex flex-col items-center w-full m-2'>
                <h1 className="text-2xl font-bold mb-1">Location?</h1>
                <RadioGroup
                  value={formValues.current_location}
                  onValueChange={(e) => setFormValues({
                    ...formValues,
                    current_location: e.value,
                    location: isOnTheLot(e.value) ? '' : formValues.location,
                  })}
                  display="flex"
                  flexDirection={{ base: "column", sm: "row" }}
                  gap={{ base: 2, sm: 6 }}
                >
                  {locationOptions.map(([value, label]) => (
                    <Radio key={value} value={value}>{label}</Radio>
                  ))}
                </RadioGroup>
              </div>
            }

            {(isLocationPrompted && !isOnTheLot(selectedLocation)) &&
              <div className='flex flex-col items-center w-full m-2'>
                <Input
                  value={formValues.location}
                  onChange={(e) => setFormValues({ ...formValues, location: e.target.value })}
                  placeholder={isAtAuction ? 'Which Auction?' : 'Where is the unit? (Leave Blank if Unknown)'}
                  borderWidth={2}
                  borderColor={colors.grey}
                  _focus={{ borderColor: colors.grey300 }}
                  bgColor={colors.black}
                  rounded="md"
                  p={2}
                />
              </div>
            }

            {(formValues.condition[0] === '2' && !isMaxRefloored(auditUnit)) &&
              <div className='flex flex-col items-center w-full m-2'>
                <h1 className="text-2xl font-bold mb-1">Can Refloor?</h1>
                <RadioGroup
                  value={formValues.can_refloor}
                  onValueChange={(e) => setFormValues({
                    ...formValues,
                    can_refloor: e.value
                  })}
                  display="flex"
                  gap={10}
                >
                  <Radio value="1">Yes</Radio>
                  <Radio value="0">No</Radio>
                </RadioGroup>
              </div>
            }

          </div>

          <NeutralButton
            text="Next"
            onClick={handleNextStep}
            dataTestId="next-button"
            disabled={!formValues.condition[0]}
          />
        </div>
      </div>
    </WizardStep>
  )
}